import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { clientReturnLinks } from './clientReturn'
import { useOnboardingStoreV2, flushOnboardingBeforeRedirect } from '@forwardflow/store/onboardingStoreV2'
import { PricingStepV3 } from '@forwardflow/modules/onboarding/steps/PricingStepV3'
import { PaymentDetails } from '@forwardflow/modules/onboarding/steps/PaymentDetails'
import { FubConnect } from '@forwardflow/modules/onboarding/steps/FubConnect'
import { CalendarConnection } from '@forwardflow/modules/onboarding/steps/CalendarConnection'
import { Button, Stack } from '@forwardflow/ui-v2'
import { OnboardingFrame } from '@forwardflow/layouts/OnboardingLayout'
import { Stepper } from '@forwardflow/modules/onboarding/Stepper'
import type { TOnboardingStep } from '@forwardflow/types/onboarding'

/** Navigation belongs to this redeemed handoff, not the dashboard's step cursor. */
export const PaymentJourney = ({ notices, blocked = false }: { notices?: React.ReactNode; blocked?: boolean } = {}) => {
   const [planConfirmed, setPlanConfirmed] = useState(false)
   const [reviewPayment, setReviewPayment] = useState(false)
   const [calendarVerified, setCalendarVerified] = useState(false)
   const { onboardingData } = useOnboardingStoreV2()
   const step: TOnboardingStep = onboardingData.isSubscriptionActivated !== true
      ? planConfirmed ? 'payment-details' : 'plan-selection'
      : reviewPayment ? 'payment-details' : onboardingData.isFUBConnected !== true ? 'fub-connect' : 'calendar-connection'
   const frame = (content: React.ReactNode) => (
      <OnboardingFrame lockOrganization stepper={<Stepper step={step} steps={['plan-selection', 'payment-details', 'fub-connect', 'calendar-connection']} readOnly />}>
         {notices}
         {!blocked && content}
      </OnboardingFrame>
   )

   if (onboardingData.isSubscriptionActivated !== true) {
      return frame(planConfirmed ? (
         <PaymentDetails fromAssistant onBack={() => setPlanConfirmed(false)} />
      ) : (
         <PricingStepV3 onConfirmed={() => setPlanConfirmed(true)} />
      ))
   }

   if (reviewPayment) return frame(<PaymentDetails onContinue={() => setReviewPayment(false)} />)
   if (onboardingData.isFUBConnected !== true) return frame(<FubConnect onBack={() => setReviewPayment(true)} />)
   if (!calendarVerified) return frame(<CalendarConnection returnTo={window.location.href} beforeConnect={flushOnboardingBeforeRedirect} onConnected={() => setCalendarVerified(true)} onBack={() => setReviewPayment(true)} />)

   return frame(<JourneyComplete />)
}

export const CalendarJourney = ({ notices, blocked = false }: { notices?: React.ReactNode; blocked?: boolean }) => {
   const [verified, setVerified] = useState(false)
   const navigate = useNavigate()
   return <OnboardingFrame lockOrganization stepper={<Stepper step="calendar-connection" steps={['calendar-connection']} readOnly />}>
      {notices}
      {!blocked && (verified ? <JourneyComplete calendarOnly /> : <CalendarConnection returnTo={window.location.href} beforeConnect={flushOnboardingBeforeRedirect} onConnected={() => setVerified(true)} onBack={() => navigate('/onboarding')} />)}
   </OnboardingFrame>
}

const JourneyComplete = ({ calendarOnly = false }: { calendarOnly?: boolean }) => {
   const navigate = useNavigate()
   const { search } = useLocation()
   const returnLinks = clientReturnLinks(search)
   const { onboardingData } = useOnboardingStoreV2()
   return (
      <Stack gap={16}>
         <h2 className="text-xl font-semibold">{calendarOnly ? 'Your calendar is connected' : 'Payment, Follow Up Boss and calendar are ready'}</h2>
         <p>
            Continue setting up your workspace here, or return to your existing
            ChatGPT or Claude conversation. Your progress is saved.
         </p>
         <Button onClick={() => {
            navigate('/onboarding', { replace: true, state: {
               onboardingOrigin: 'assistant',
               organizationId: onboardingData.organizationId,
               resumeAfterPayment: true,
            } })
         }}>
            Continue in dashboard
         </Button>
         <div className="flex flex-wrap gap-3">
            {returnLinks.map(client => (
               <a key={client.id} href={client.href} target="_blank" rel="noopener noreferrer" className="text-white underline underline-offset-4">
                  {client.label}
               </a>
            ))}
         </div>
         <p className="text-sm text-white/70">
            Open your original conversation to continue setup. These links open
            the app, not a specific conversation. You can close this tab.
         </p>
      </Stack>
   )
}
